/**
 * shared/taxonomy.js — NGUỒN DUY NHẤT cho mọi enum của hệ thống.
 *
 * Nguyên tắc:
 *  - Mã (id) là tiếng Anh, viết hoa, KHÔNG BAO GIỜ đổi sau khi đã publish.
 *    Đổi mã = hỏng benchmark cũ, hỏng khớp quỹ cũ, hỏng snapshot cũ.
 *  - Nhãn hiển thị nằm ở `vi` (và `en` để mở rộng sau).
 *  - `aliases` phục vụ chuẩn hoá dữ liệu nhập tay / AI bóc tách từ deck.
 *  - Bỏ một mã: đặt `deprecated: true`, KHÔNG xoá khỏi file.
 *
 * File này không import gì, không phụ thuộc Firebase, dùng được ở cả trình duyệt
 * lẫn Cloud Functions.
 */

/* ------------------------------------------------------------------ */
/* Tiện ích nội bộ                                                      */
/* ------------------------------------------------------------------ */

/** Bỏ dấu tiếng Việt + hạ chữ thường + gom khoảng trắng. */
export function boDau(s) {
  if (typeof s !== 'string') return '';
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function def(list) {
  const byId = Object.create(null);
  const byAlias = Object.create(null);
  for (const item of list) {
    if (byId[item.id]) throw new Error(`Trùng mã trong taxonomy: ${item.id}`);
    byId[item.id] = Object.freeze(item);
    const keys = [item.id, item.vi, item.en, ...(item.aliases || [])];
    for (const k of keys) {
      if (!k) continue;
      const norm = boDau(k);
      if (norm) byAlias[norm] = item.id;
    }
  }
  return {
    list: Object.freeze(list.map((i) => Object.freeze(i))),
    ids: Object.freeze(list.map((i) => i.id)),
    byId: Object.freeze(byId),
    byAlias: Object.freeze(byAlias),
  };
}

/* ------------------------------------------------------------------ */
/* Ngành                                                                */
/* ------------------------------------------------------------------ */

export const INDUSTRY = def([
  { id: 'FINTECH',       vi: 'Công nghệ tài chính',      en: 'Fintech',        aliases: ['fintech', 'thanh toan', 'payment', 'ngan hang so'] },
  { id: 'INSURTECH',     vi: 'Công nghệ bảo hiểm',       en: 'Insurtech',      aliases: ['bao hiem'] },
  { id: 'EDTECH',        vi: 'Công nghệ giáo dục',       en: 'Edtech',         aliases: ['giao duc', 'edu'] },
  { id: 'HEALTHTECH',    vi: 'Công nghệ y tế',           en: 'Healthtech',     aliases: ['y te', 'medtech', 'suc khoe'] },
  { id: 'BIOTECH',       vi: 'Công nghệ sinh học',       en: 'Biotech',        aliases: ['sinh hoc'] },
  { id: 'ECOMMERCE',     vi: 'Thương mại điện tử',       en: 'E-commerce',     aliases: ['tmdt', 'ecom', 'ban le online'] },
  { id: 'RETAILTECH',    vi: 'Công nghệ bán lẻ',         en: 'Retail tech',    aliases: ['ban le'] },
  { id: 'LOGISTICS',     vi: 'Logistics & chuỗi cung ứng', en: 'Logistics',    aliases: ['van tai', 'chuoi cung ung', 'supply chain'] },
  { id: 'MOBILITY',      vi: 'Giao thông & di chuyển',   en: 'Mobility',       aliases: ['di chuyen', 'xe dien', 'ev'] },
  { id: 'PROPTECH',      vi: 'Công nghệ bất động sản',   en: 'Proptech',       aliases: ['bat dong san'] },
  { id: 'AGRITECH',      vi: 'Công nghệ nông nghiệp',    en: 'Agritech',       aliases: ['nong nghiep', 'agtech'] },
  { id: 'FOODBEV',       vi: 'Thực phẩm & đồ uống',      en: 'Food & beverage', aliases: ['f b', 'fnb', 'do uong', 'thuc pham'] },
  { id: 'SAAS_B2B',      vi: 'Phần mềm doanh nghiệp',    en: 'B2B SaaS',       aliases: ['saas', 'phan mem doanh nghiep', 'enterprise software'] },
  { id: 'AI',            vi: 'AI & dữ liệu lớn',         en: 'AI & Big Data',  aliases: ['ai', 'tri tue nhan tao', 'machine learning', 'big data', 'du lieu lon'] },
  { id: 'SEMICONDUCTOR', vi: 'Vi mạch bán dẫn',          en: 'Semiconductor',  aliases: ['ban dan', 'chip', 'vi mach'] },
  { id: 'ROBOTICS',      vi: 'Robot & tự động hoá',      en: 'Robotics',       aliases: ['robot', 'tu dong hoa', 'automation'] },
  { id: 'MATERIALS',     vi: 'Vật liệu mới',             en: 'Advanced materials', aliases: ['vat lieu'] },
  { id: 'ENERGY',        vi: 'Năng lượng',               en: 'Energy',         aliases: ['nang luong', 'dien mat troi'] },
  { id: 'CLIMATE',       vi: 'Khí hậu & môi trường',     en: 'Climate tech',   aliases: ['moi truong', 'net zero', 'esg'] },
  { id: 'CYBERSECURITY', vi: 'An ninh mạng',             en: 'Cybersecurity',  aliases: ['bao mat', 'an ninh mang', 'security'] },
  { id: 'HRTECH',        vi: 'Công nghệ nhân sự',        en: 'HR tech',        aliases: ['nhan su', 'tuyen dung'] },
  { id: 'MARTECH',       vi: 'Công nghệ marketing',      en: 'Martech',        aliases: ['marketing', 'quang cao', 'adtech'] },
  { id: 'LEGALTECH',     vi: 'Công nghệ pháp lý',        en: 'Legaltech',      aliases: ['phap ly', 'luat'] },
  { id: 'GAMING',        vi: 'Game',                     en: 'Gaming',         aliases: ['game', 'esports'] },
  { id: 'MEDIA',         vi: 'Truyền thông & nội dung',  en: 'Media & content', aliases: ['noi dung', 'truyen thong', 'content'] },
  { id: 'TRAVEL',        vi: 'Du lịch',                  en: 'Travel',         aliases: ['du lich', 'traveltech'] },
  { id: 'WEB3',          vi: 'Blockchain & Web3',        en: 'Blockchain',     aliases: ['blockchain', 'crypto', 'web3'] },
  { id: 'MANUFACTURING', vi: 'Sản xuất & công nghiệp',   en: 'Manufacturing',  aliases: ['san xuat', 'cong nghiep', 'industry 4 0'] },
  { id: 'OTHER',         vi: 'Khác',                     en: 'Other',          aliases: ['khac'] },
]);

/* ------------------------------------------------------------------ */
/* Giai đoạn gọi vốn — CÓ THỨ TỰ (dùng để khớp quỹ)                     */
/* ------------------------------------------------------------------ */

export const STAGE = def([
  { id: 'IDEA',      vi: 'Ý tưởng',       en: 'Idea',       rank: 0, aliases: ['y tuong'] },
  { id: 'PROTOTYPE', vi: 'Nguyên mẫu',    en: 'Prototype',  rank: 1, aliases: ['nguyen mau', 'poc'] },
  { id: 'ANGEL',     vi: 'Angel',         en: 'Angel',      rank: 2, aliases: ['thien than', 'angel round'] },
  { id: 'PRESEED',   vi: 'Pre-seed',      en: 'Pre-seed',   rank: 3, aliases: ['pre seed', 'tien hat giong'] },
  { id: 'SEED',      vi: 'Seed',          en: 'Seed',       rank: 4, aliases: ['hat giong', 'vong hat giong'] },
  { id: 'SEED_PLUS', vi: 'Seed mở rộng',  en: 'Seed+',      rank: 5, aliases: ['seed extension', 'bridge'] },
  { id: 'SERIES_A',  vi: 'Series A',      en: 'Series A',   rank: 6, aliases: ['series a', 'vong a'] },
  { id: 'LATER',     vi: 'Series B trở lên', en: 'Series B+', rank: 7, aliases: ['series b', 'growth'] },
]);

/** So sánh hai giai đoạn: âm nếu a sớm hơn b. */
export function stageRank(id) {
  const s = STAGE.byId[id];
  return s ? s.rank : -1;
}

/* Giai đoạn sản phẩm — TÁCH RỜI giai đoạn gọi vốn (xem đặc tả mục 3.2) */
export const PRODUCT_STAGE = def([
  { id: 'IDEA',      vi: 'Mới là ý tưởng',       en: 'Idea',      rank: 0 },
  { id: 'PROTOTYPE', vi: 'Có nguyên mẫu',        en: 'Prototype', rank: 1 },
  { id: 'MVP',       vi: 'Có MVP, có người dùng', en: 'MVP',      rank: 2 },
  { id: 'LIVE',      vi: 'Đã bán ra thị trường', en: 'Live',      rank: 3 },
  { id: 'SCALING',   vi: 'Đang mở rộng',         en: 'Scaling',   rank: 4 },
]);

/* ------------------------------------------------------------------ */
/* Mô hình kinh doanh & khách hàng                                      */
/* ------------------------------------------------------------------ */

export const BUSINESS_MODEL = def([
  { id: 'SAAS',        vi: 'Thuê bao phần mềm (SaaS)', en: 'SaaS',        aliases: ['thue bao', 'subscription'] },
  { id: 'MARKETPLACE', vi: 'Sàn giao dịch',            en: 'Marketplace', aliases: ['san', 'nen tang trung gian'] },
  { id: 'ECOMMERCE',   vi: 'Bán hàng trực tiếp',       en: 'E-commerce',  aliases: ['ban hang'] },
  { id: 'D2C',         vi: 'Thương hiệu D2C',          en: 'D2C',         aliases: ['dtc', 'd2c'] },
  { id: 'HARDWARE',    vi: 'Phần cứng / thiết bị',     en: 'Hardware',    aliases: ['phan cung', 'thiet bi'] },
  { id: 'SERVICE',     vi: 'Dịch vụ',                  en: 'Service',     aliases: ['dich vu', 'agency'] },
  { id: 'PLATFORM',    vi: 'Nền tảng / API',           en: 'Platform',    aliases: ['nen tang', 'api'] },
  { id: 'LICENSING',   vi: 'Cấp phép công nghệ',       en: 'Licensing',   aliases: ['ip licensing', 'chuyen giao'] },
  { id: 'ADS',         vi: 'Quảng cáo',                en: 'Advertising', aliases: ['quang cao'] },
  { id: 'TRANSACTION', vi: 'Thu phí giao dịch',        en: 'Transaction fee', aliases: ['phi giao dich', 'take rate'] },
  { id: 'OTHER',       vi: 'Khác',                     en: 'Other' },
]);

export const CUSTOMER_TYPE = def([
  { id: 'B2B',   vi: 'Bán cho doanh nghiệp',      en: 'B2B' },
  { id: 'B2C',   vi: 'Bán cho người tiêu dùng',   en: 'B2C' },
  { id: 'B2B2C', vi: 'Qua đối tác tới người dùng', en: 'B2B2C' },
  { id: 'B2G',   vi: 'Bán cho khối nhà nước',     en: 'B2G', aliases: ['nha nuoc', 'chinh phu'] },
]);

/* ------------------------------------------------------------------ */
/* Pháp lý & sở hữu                                                     */
/* ------------------------------------------------------------------ */

export const ENTITY_TYPE = def([
  { id: 'NONE',    vi: 'Chưa lập công ty',           en: 'Not incorporated', aliases: ['chua thanh lap'] },
  { id: 'HKD',     vi: 'Hộ kinh doanh',              en: 'Household business' },
  { id: 'LLC1',    vi: 'TNHH một thành viên',        en: 'Single-member LLC', aliases: ['tnhh mtv', 'tnhh 1 tv'] },
  { id: 'LLC2',    vi: 'TNHH hai thành viên trở lên', en: 'Multi-member LLC', aliases: ['tnhh 2 tv'] },
  { id: 'JSC',     vi: 'Cổ phần',                    en: 'Joint stock company', aliases: ['cp', 'co phan'] },
  { id: 'FOREIGN', vi: 'Pháp nhân nước ngoài',       en: 'Foreign entity', aliases: ['singapore', 'offshore'] },
]);

export const HOLDER_TYPE = def([
  { id: 'FOUNDER',   vi: 'Đồng sáng lập',    en: 'Founder' },
  { id: 'EMPLOYEE',  vi: 'Nhân sự',          en: 'Employee' },
  { id: 'ANGEL',     vi: 'Nhà đầu tư thiên thần', en: 'Angel' },
  { id: 'VC',        vi: 'Quỹ đầu tư',       en: 'VC' },
  { id: 'ESOP',      vi: 'Quỹ cổ phiếu thưởng', en: 'Option pool' },
  { id: 'ADVISOR',   vi: 'Cố vấn',           en: 'Advisor' },
  { id: 'CORPORATE', vi: 'Đối tác doanh nghiệp', en: 'Corporate' },
  { id: 'OTHER',     vi: 'Khác',             en: 'Other' },
]);

export const INSTRUMENT = def([
  { id: 'EQUITY',      vi: 'Mua cổ phần trực tiếp', en: 'Priced equity', aliases: ['co phan'] },
  { id: 'SAFE',        vi: 'SAFE',                  en: 'SAFE', aliases: ['safe note'] },
  { id: 'CONVERTIBLE', vi: 'Trái phiếu chuyển đổi', en: 'Convertible note', aliases: ['convertible note', 'trai phieu chuyen doi'] },
  { id: 'GRANT',       vi: 'Tài trợ không hoàn lại', en: 'Grant', aliases: ['grant', 'tai tro'] },
  { id: 'LOAN',        vi: 'Vay',                   en: 'Loan', aliases: ['vay von'] },
]);

export const LEGAL_DOC_STATUS = def([
  { id: 'NONE',  vi: 'Chưa có',        en: 'Not started' },
  { id: 'DRAFT', vi: 'Đang soạn',      en: 'Draft' },
  { id: 'DONE',  vi: 'Đã ký / hoàn tất', en: 'Done' },
  { id: 'NA',    vi: 'Không áp dụng',  en: 'N/A' },
]);

/* ------------------------------------------------------------------ */
/* Quỹ, địa bàn, chủ đề công cụ                                         */
/* ------------------------------------------------------------------ */

/**
 * Loại vốn — chiều phân biệt căn bản mà bộ taxonomy ban đầu thiếu.
 *
 * Toàn bộ công cụ được xây quanh giả định founder đi gọi vốn cổ phần. Nhưng
 * phần lớn doanh nghiệp Việt Nam không phù hợp với vốn mạo hiểm: thị trường
 * vừa phải, tăng trưởng đều, dòng tiền dương. Với họ, khoản vay hoặc tài trợ
 * mới là nguồn đúng — và điểm khác biệt lớn nhất là KHÔNG PHA LOÃNG.
 *
 * Không có chiều này, công cụ khớp quỹ sẽ đặt một quỹ cho vay cạnh một quỹ
 * mạo hiểm như thể chúng là cùng một thứ.
 */
export const CAPITAL_TYPE = def([
  { id: 'EQUITY',  vi: 'Vốn cổ phần', en: 'Equity', aliases: ['co phan'] },
  { id: 'DEBT',    vi: 'Vốn vay',     en: 'Debt', aliases: ['cho vay', 'loan'] },
  { id: 'GRANT',   vi: 'Tài trợ',     en: 'Grant', aliases: ['tai tro'] },
  { id: 'BLENDED', vi: 'Kết hợp',     en: 'Blended', aliases: ['ket hop'] },
]);

/**
 * Mức độ hiện diện tại Việt Nam.
 *
 * Danh sách bắt đầu mở rộng sang quỹ khu vực Đông Nam Á. Không phân biệt thì
 * founder pre-seed Việt Nam sẽ thấy một quỹ Singapore séc từ 500 nghìn USD nằm
 * cạnh một quỹ có văn phòng ở TP.HCM như thể hai nơi đó ngang nhau — và gửi
 * hồ sơ vào nơi thứ nhất gần như chắc chắn không có kết quả.
 */
export const VN_PRESENCE = def([
  { id: 'OFFICE',      vi: 'Có văn phòng tại Việt Nam', en: 'Office in Vietnam' },
  { id: 'INVESTED',    vi: 'Đã đầu tư tại Việt Nam',    en: 'Has invested in Vietnam' },
  { id: 'ANNOUNCED',   vi: 'Đã công bố kế hoạch vào Việt Nam',
    en: 'Announced Vietnam plans' },
  { id: 'REGION_ONLY', vi: 'Quỹ khu vực, chưa thấy hoạt động tại Việt Nam',
    en: 'Regional fund, no Vietnam activity found' },
]);

export const FUND_TYPE = def([
  { id: 'ANGEL',         vi: 'Nhà đầu tư cá nhân', en: 'Angel' },
  { id: 'ANGEL_NETWORK', vi: 'Mạng lưới thiên thần', en: 'Angel network' },
  { id: 'VC',            vi: 'Quỹ đầu tư mạo hiểm', en: 'VC' },
  { id: 'CVC',           vi: 'Quỹ của tập đoàn',   en: 'Corporate VC' },
  { id: 'ACCELERATOR',   vi: 'Vườn ươm / tăng tốc', en: 'Accelerator', aliases: ['incubator', 'vuon uom'] },
  { id: 'GRANT',         vi: 'Chương trình tài trợ', en: 'Grant program' },
  { id: 'GOV',           vi: 'Quỹ nhà nước',       en: 'Government fund' },
  { id: 'IMPACT_VC',     vi: 'Quỹ đầu tư tác động', en: 'Impact VC' },
  { id: 'PE_GROWTH',     vi: 'Quỹ đầu tư tăng trưởng', en: 'Growth equity',
    aliases: ['private equity', 'pe'] },
  // Đơn vị chỉ cấp vốn vay / hoàn trả theo doanh thu — không lấy cổ phần.
  { id: 'LENDER',        vi: 'Vốn vay / hoàn trả', en: 'Debt / revenue-based financing',
    aliases: ['cho vay', 'lender', 'revenue based'] },
]);

export const GEO = def([
  { id: 'VN-HCM',  vi: 'TP. Hồ Chí Minh', en: 'Ho Chi Minh City', aliases: ['hcm', 'tphcm', 'sai gon', 'ho chi minh'] },
  { id: 'VN-HN',   vi: 'Hà Nội',          en: 'Hanoi', aliases: ['ha noi'] },
  { id: 'VN-DN',   vi: 'Đà Nẵng',         en: 'Da Nang', aliases: ['da nang'] },
  { id: 'VN',      vi: 'Việt Nam (toàn quốc)', en: 'Vietnam', aliases: ['viet nam', 'vn', 'toan quoc'] },
  { id: 'SEA',     vi: 'Đông Nam Á',      en: 'Southeast Asia', aliases: ['dong nam a', 'sea'] },
  { id: 'APAC',    vi: 'Châu Á - Thái Bình Dương', en: 'APAC' },
  { id: 'GLOBAL',  vi: 'Toàn cầu',        en: 'Global', aliases: ['toan cau'] },
]);

export const TOOL_TOPIC = def([
  { id: 'FUNDRAISING', vi: 'Gọi vốn',    en: 'Fundraising' },
  { id: 'FINANCE',     vi: 'Tài chính',  en: 'Finance' },
  { id: 'MARKET',      vi: 'Thị trường', en: 'Market' },
  { id: 'GOVERNANCE',  vi: 'Quản trị',   en: 'Governance' },
  { id: 'LEGAL',       vi: 'Pháp lý',    en: 'Legal' },
]);

export const USE_OF_FUNDS = def([
  { id: 'PRODUCT',   vi: 'Phát triển sản phẩm', en: 'Product' },
  { id: 'HIRING',    vi: 'Tuyển người',         en: 'Hiring' },
  { id: 'GTM',       vi: 'Bán hàng & marketing', en: 'Go-to-market' },
  { id: 'OPS',       vi: 'Vận hành',            en: 'Operations' },
  { id: 'INVENTORY', vi: 'Hàng tồn / nguyên vật liệu', en: 'Inventory' },
  { id: 'LEGAL',     vi: 'Pháp lý & sở hữu trí tuệ', en: 'Legal & IP' },
  { id: 'BUFFER',    vi: 'Dự phòng',            en: 'Buffer' },
]);

export const EVIDENCE_TYPE = def([
  { id: 'LOI',        vi: 'Thư cam kết mua (LOI)', en: 'Letter of intent', aliases: ['loi', 'thu cam ket'] },
  { id: 'MOU',        vi: 'Biên bản ghi nhớ',      en: 'MOU', aliases: ['mou', 'bien ban ghi nho'] },
  { id: 'PILOT',      vi: 'Triển khai thử',        en: 'Pilot', aliases: ['pilot', 'chay thu'] },
  { id: 'PAID_PILOT', vi: 'Triển khai thử có thu phí', en: 'Paid pilot' },
  { id: 'PREORDER',   vi: 'Đơn đặt trước',         en: 'Pre-order', aliases: ['dat truoc'] },
  { id: 'CONTRACT',   vi: 'Hợp đồng đã ký',        en: 'Signed contract', aliases: ['hop dong'] },
  { id: 'WAITLIST',   vi: 'Danh sách chờ',         en: 'Waitlist' },
  { id: 'AWARD',      vi: 'Giải thưởng / tài trợ', en: 'Award or grant' },
  { id: 'PATENT',     vi: 'Bằng sáng chế / IP',    en: 'Patent or IP' },
]);

/* ------------------------------------------------------------------ */
/* Chuẩn hoá                                                            */
/* ------------------------------------------------------------------ */

const REGISTRY = {
  INDUSTRY, STAGE, PRODUCT_STAGE, BUSINESS_MODEL, CUSTOMER_TYPE, ENTITY_TYPE,
  HOLDER_TYPE, INSTRUMENT, LEGAL_DOC_STATUS, FUND_TYPE, GEO, TOOL_TOPIC,
  USE_OF_FUNDS, EVIDENCE_TYPE, CAPITAL_TYPE, VN_PRESENCE,
};

/**
 * Chuẩn hoá một chuỗi tự do về mã enum.
 * Dùng cho: import file, AI bóc tách deck, người dùng gõ tay.
 * Trả về null nếu không nhận ra — KHÔNG đoán bừa, để lớp trên hỏi lại người dùng.
 */
export function normalize(taxonomyName, raw) {
  const t = REGISTRY[taxonomyName];
  if (!t) throw new Error(`Không có taxonomy: ${taxonomyName}`);
  if (raw == null) return null;
  return t.byAlias[boDau(String(raw))] ?? null;
}

/** Kiểm tra mã hợp lệ. Dùng trong validator schema và Firestore rules test. */
export function isValid(taxonomyName, id) {
  const t = REGISTRY[taxonomyName];
  if (!t) throw new Error(`Không có taxonomy: ${taxonomyName}`);
  return Object.prototype.hasOwnProperty.call(t.byId, id);
}

/** Nhãn hiển thị theo ngôn ngữ. */
export function label(taxonomyName, id, locale = 'vi') {
  const t = REGISTRY[taxonomyName];
  const item = t && t.byId[id];
  if (!item) return id;
  return item[locale] || item.vi || item.id;
}

/** Danh sách để đổ vào dropdown. */
export function options(taxonomyName, locale = 'vi') {
  const t = REGISTRY[taxonomyName];
  if (!t) throw new Error(`Không có taxonomy: ${taxonomyName}`);
  return t.list
    .filter((i) => !i.deprecated)
    .map((i) => ({ value: i.id, label: i[locale] || i.vi }));
}

export const TAXONOMIES = Object.freeze(Object.keys(REGISTRY));
